'use client';

import { type FC, type ReactNode, useCallback, useEffect, useState } from 'react';
import type { EmblaOptionsType } from 'embla-carousel-react';
import useEmblaCarousel from 'embla-carousel-react';

import useIsMobile from '@hyex/utils/hooks/useIsMobile';

import { NextButton, PrevButton } from '../buttons';

type PropTypes = {
  slides: () => ReactNode;
  prefix?: string;
  activeClassName?: string;
  alwaysShowArrows: boolean;

  containerClassName?: string;
  wrapperClassName?: string;
  parentClassName?: string;

  options: EmblaOptionsType;
  arrowColor?: string;
};

const ArrowCarousel: FC<PropTypes> = ({
  slides,
  containerClassName,
  wrapperClassName,
  parentClassName,
  alwaysShowArrows,
  options,
  arrowColor = '#ffffff',
}) => {
  const isMobile = useIsMobile(1340);

  const [viewportRef, embla] = useEmblaCarousel(options);

  const [prevBtnEnabled, setPrevBtnEnabled] = useState(false);

  const [nextBtnEnabled, setNextBtnEnabled] = useState(false);

  const scrollPrev = useCallback(() => embla && embla.scrollPrev(), [embla]);

  const scrollNext = useCallback(() => embla && embla.scrollNext(), [embla]);

  const onSelect = useCallback(() => {
    if (!embla) return;

    setPrevBtnEnabled(embla.canScrollPrev());

    setNextBtnEnabled(embla.canScrollNext());
  }, [embla]);

  const onScroll = useCallback(() => {
    if (!embla) return;
  }, [embla]);

  useEffect(() => {
    if (!embla) return;

    onSelect();

    onScroll();

    embla.on('select', onSelect);

    embla.on('scroll', onScroll);
  }, [embla, onSelect, onScroll]);

  return (
    <div className={parentClassName}>
      <div className={containerClassName}>
        {isMobile || alwaysShowArrows ? (
          <>
            <div className={`embla ${wrapperClassName}`}>
              <div className="embla-viewport" ref={viewportRef}>
                <div className="embla-container">{slides()}</div>
              </div>
            </div>
            <div className="embla-buttons">
              <PrevButton onClick={scrollPrev} enabled={prevBtnEnabled} />
              <NextButton onClick={scrollNext} enabled={nextBtnEnabled} />
            </div>
          </>
        ) : (
          <div className={`${wrapperClassName}`}>
            <div className="embla-container">{slides()}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ArrowCarousel;
